import { apiClient } from './index';

/**
 * Transaction VOID API Service
 * Handles all void-related API calls
 *
 * @description Routes to /api/voids/* endpoints for void operations
 *
 * @dependencies
 * - apiClient: Axios instance with auth headers
 *
 * @notes
 * - Backend void routes are mounted at /api/voids (see routes/voids.js)
 * - Separate from transaction routes for clean separation of concerns
 */

const voidService = {
  /**
   * Check if a transaction is eligible for void
   * @param {number} transactionId - Transaction ID
   * @returns {Promise<Object>} Void eligibility details
   */
  checkVoidEligibility: async (transactionId) => {
    try {
      const response = await apiClient.get(`/voids/eligibility/${transactionId}`);
      return response.data.eligibility;
    } catch (error) {
      console.error('Error checking void eligibility:', error);
      throw error;
    }
  },

  /**
   * Process a void request
   * @param {number} transactionId - Transaction ID to void
   * @param {string} reason - Reason for void
   * @returns {Promise<Object>} Void result
   */
  processVoid: async (transactionId, reason) => {
    try {
      const response = await apiClient.post(`/voids/${transactionId}`, {
        reason: reason
      });
      return response.data;
    } catch (error) {
      console.error('Error processing void:', error);
      throw error;
    }
  },

  /**
   * Get void history for a transaction
   * @param {number} transactionId - Transaction ID
   * @returns {Promise<Object>} Void attempt history
   */
  getVoidHistory: async (transactionId) => {
    try {
      const response = await apiClient.get(`/voids/history/${transactionId}`);
      return response.data.history;
    } catch (error) {
      console.error('Error getting void history:', error);
      throw error;
    }
  },

  /**
   * Get void reasons for dropdown
   * @returns {Array<Object>} Available void reasons
   */
  getVoidReasons: () => {
    return [
      { value: 'duplicate_transaction', label: 'Duplicate transaction' },
      { value: 'incorrect_amount', label: 'Incorrect amount' },
      { value: 'customer_request', label: 'Customer request' },
      { value: 'technical_error', label: 'Technical error' },
      { value: 'wrong_payment_method', label: 'Wrong payment method' },
      { value: 'entry_error', label: 'Entry error' },
      { value: 'other', label: 'Other (specify below)' }
    ];
  }
};

export default voidService;